import EventListenerModel from "../../../utils/EventListenerModel";

/**
 * Represents a sub question for a service type
 */
class ServiceTypeOptionHost extends EventListenerModel {
  serviceHost = undefined;
  title = "";
  dataKey = "";
  type = "radio";
  values = [];
  value = undefined;
  inputParams = {};
  validationErrorMessage = "";
  onValidate = undefined;

  constructor(opt = {}, serviceHost) {
    super();
    this.serviceHost = serviceHost;
    this.title = opt.title;
    this.dataKey = opt.dataKey;
    this.values = opt.values || [];
    this.type = opt.type;
    this.onValidate = opt.onValidate;
    this.canShow = typeof opt.canShow === "function" ? opt.canShow : null;
    this.validationErrorMessage = opt.validationErrorMessage;
    this.switchServiceToTow = opt.switchServiceToTow;
    this.inputParams = opt.inputParams || {};
  }

  getOptionByDataKey(dataKey) {
    return this.serviceHost.options?.find((opt) => opt.dataKey === dataKey);
  }

  setValue(val) {
    this.value = val;
    this.isVisible();
    this.processEventListener("onValueChange", this.value);
    this.serviceHost.onOptionValueChange(this);
  }

  validate() {
    const isValid = typeof this.onValidate === "function" ? this.onValidate(this) : true;
    this.processEventListener("onValidate", this.value);
    return isValid;
  }

  isVisible() {
    if (typeof this.canShow !== "function") {
      return true;
    } else {
      return this.canShow(this);
    }
  }

  switchTireChangeToTow() {
    if (typeof this.switchServiceToTow === "function" && this.switchServiceToTow === true) {
      this.setValue("Tow");
    }
  }
}

/**
 * Represents a service type
 */
class ServiceTypeHost extends EventListenerModel {
  dataHost = undefined;
  label = "";
  value = undefined;
  options = [];
  icon = "";
  iconAlt = "";
  selected = false;

  constructor(service = {}, dataHost) {
    super();
    this.dataHost = dataHost;
    this.label = service.label;
    this.icon = service.icon;
    this.iconAlt = service.iconAlt;
    this.value = service.value;
    this.selected = service.selected;
    this.options = (service.options || []).map((opt) => {
      return new ServiceTypeOptionHost(opt, this);
    });
  }

  getOptionByDataKey(dataKey) {
    return this.options?.find((opt) => opt.dataKey === dataKey);
  }

  onOptionValueChange(optionHost) {
    this.processEventListener("onOptionValueChange", optionHost);
  }

  setSelected(val) {
    const oldValue = this.selected;
    if (!!val === oldValue) {
      return;
    }

    this.selected = !!val;
    this.processEventListener("onSelectedChanged", this.selected);

    if (!this.selected) {
      return;
    }
    for (let i = 0; i < this.dataHost.services.length; i++) {
      const service = this.dataHost.services[i];
      if (service === this) {
        continue;
      }
      //Deselected all other services
      service.setSelected(false);
    }
  }

  validate() {
    let isValidated = true;

    for (let i = 0; i < this.options.length; i++) {
      const opt = this.options[i];
      const isValid = opt.validate();
      if (!isValid) {
        isValidated = false;
      }
    }

    return isValidated;
  }
}

/**
 * Service Type Collection
 */
class ServiceTypeDataHost extends EventListenerModel {
  services = [];

  //For each service, it creates a ServiceTypeHost, linking it back to ServiceTypeDataHost
  constructor(services = []) {
    super();
    this.services = services.map((service) => {
      return new ServiceTypeHost(service, this);
    });
  }

  addService(service) {
    this.services.push(service);
    this.processEventListener("onServiceAdded", service);
  }

  getData() {
    const data = {};
    for (let i = 0; i < this.services.length; i++) {
      const service = this.services[i];
      if (!service.selected) {
        continue;
      }

      data.serviceType = service.value; //service type, ex: tow, winch
      const options = [];

      for (let j = 0; j < service.options.length; j++) {
        const opt = service.options[j];

        if (opt.dataKey === "serviceType") {
          data.serviceType = opt.value;
        } else {
          data[opt.dataKey] = opt.value;
          options.push({
            key: opt.dataKey,
            value: opt.value,
          });
        }
      }
      data.additionalDetails = options;
      break;
    }

    return data;
  }

  validate() {
    let isValidated = true;

    for (let i = 0; i < this.services.length; i++) {
      const service = this.services[i];
      if (!service.selected) {
        continue;
      }

      isValidated = service.validate();
    }

    return isValidated;
  }
}

export { ServiceTypeDataHost, ServiceTypeHost, ServiceTypeOptionHost };
